import React, { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, ShieldAlert, ShieldCheck, Shield } from "lucide-react";

const RISK = {
  low: { icon: ShieldCheck, color: "text-emerald-400", bg: "bg-emerald-400/10", border: "border-emerald-400/30" },
  medium: { icon: Shield, color: "text-yellow-400", bg: "bg-yellow-400/10", border: "border-yellow-400/30" },
  high: { icon: ShieldAlert, color: "text-red-400", bg: "bg-red-400/10", border: "border-red-400/30" },
};

export default function Scanner() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a contract PDF first");
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const token = localStorage.getItem("dap_token");
      const r = await axios.post(`${API}/scan/contract`, fd, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  const risk = result ? RISK[result.overall_risk] || RISK.medium : null;
  const RiskIcon = risk?.icon || Shield;

  return (
    <div className="max-w-5xl mx-auto px-6 py-16">
      <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-3">// contract red flag scanner</p>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-3">
        Read it before you sign it.
      </h1>
      <p className="text-neutral-400 mb-10 max-w-2xl">
        Upload a brand contract PDF. Claude Sonnet reads every clause and flags exclusivity, usage rights,
        payment terms & more. Free: 1 scan / month.
      </p>

      <form
        onSubmit={submit}
        className="p-8 rounded-2xl bg-[#141414] border border-dashed border-white/15 mb-8"
        data-testid="scanner-form"
      >
        <label className="flex flex-col items-center justify-center gap-3 py-10 cursor-pointer" data-testid="scanner-drop-label">
          <Upload className="w-8 h-8 text-neutral-500" />
          <span className="text-sm text-neutral-300">
            {file ? file.name : "Choose a PDF contract"}
          </span>
          <input
            type="file"
            accept=".pdf,.txt"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="scanner-file-input"
          />
        </label>
        <Button
          type="submit"
          disabled={busy}
          className="w-full h-12 rounded-full bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold"
          data-testid="scanner-submit-btn"
        >
          {busy ? "Scanning… (~15s)" : "Scan for red flags"}
        </Button>
        <p className="text-[11px] font-mono text-neutral-500 mt-3 text-center">
          Flags &amp; suggestions — not legal advice.
        </p>
      </form>

      {result && (
        <div data-testid="scanner-result">
          <div className={`p-6 rounded-2xl border ${risk.bg} ${risk.border} flex items-start gap-4 mb-6`}>
            <RiskIcon className={`w-8 h-8 ${risk.color} shrink-0`} />
            <div>
              <p className={`font-mono text-xs uppercase tracking-widest ${risk.color}`}>
                Overall risk: {result.overall_risk}
              </p>
              <p className="mt-2 text-sm text-neutral-200 leading-relaxed">{result.summary}</p>
            </div>
          </div>

          <p className="font-mono text-xs uppercase tracking-widest text-neutral-500 mb-4">
            // {result.flags.length} flag(s)
          </p>
          <div className="space-y-3">
            {result.flags.map((f, i) => (
              <div
                key={i}
                className="p-5 rounded-xl bg-[#141414] border border-white/5"
                data-testid={`scanner-flag-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-display font-bold text-lg">{f.clause_title}</h4>
                  <span
                    className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded ${
                      RISK[f.severity]?.bg || "bg-white/5"
                    } ${RISK[f.severity]?.color || "text-neutral-400"}`}
                  >
                    {f.severity}
                  </span>
                </div>
                {f.quote && (
                  <blockquote className="text-xs font-mono text-neutral-500 border-l-2 border-white/10 pl-3 my-2 italic">
                    "{f.quote}"
                  </blockquote>
                )}
                <p className="text-sm text-neutral-300 mt-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-red-400 mr-2">Issue</span>
                  {f.issue}
                </p>
                <p className="text-sm text-neutral-300 mt-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#CCFF00] mr-2">Fix</span>
                  {f.suggestion}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
